import * as React from 'react';
import { observer } from 'mobx-react-lite';
import { Layout, Button, Form, Input, Spin, message } from 'antd';
import AceEditor from 'react-ace';
import 'brace/mode/mysql';
import 'brace/theme/xcode';
import LayoutHeader from '../../../../components/LayoutHeader';
import { Content } from 'antd/lib/layout/layout';
import ScriptManagementStore from '../../stores/ScriptManagementStore';
import { ScriptUsageResult, ScriptRevision } from '../../types';
import SqlScriptUsages from './SqlScriptUsages';
import ScriptRevisionHistory from './ScriptRevisionHistory';
import RevisionViewModal from './RevisionViewModal';
import DryRunModal from '../DryRunModal';
import { Utils } from '../../../common/services/Utils';
import './CreateEditScript.less';

type Props = {
    store: ScriptManagementStore;
};

const CreateEditScript: React.FC<Props> = ({ store }) => {
    const [form] = Form.useForm();
    const [isDataLoaded, setIsDataLoaded] = React.useState(false);
    const [scriptUsages, setScriptUsages] = React.useState<ScriptUsageResult[]>([]);
    const [revisions, setRevisions] = React.useState<ScriptRevision[]>([]);
    const [selectedRevision, setSelectedRevision] = React.useState<ScriptRevision | null>(null);

    const editMode = React.useMemo(() => store.selectedScriptId !== null, [store.selectedScriptId]);

    const sortedRevisions = React.useMemo(() => {
        return [...revisions].sort((a, b) => Utils.safeDateStringCompare(b.createDate, a.createDate));
    }, [revisions]);

    const getSqlBody = React.useCallback(() => {
        return form.getFieldValue('body') || '';
    }, [form]);

    React.useEffect(() => {
        const loadScriptUsages = async () => {
            if (editMode && store.selectedScriptId) {
                try {
                    const usages = await store.checkScriptUsage(store.selectedScriptId);
                    setScriptUsages(usages);
                } catch (error) {
                    console.error('Failed to load script usages:', error);
                    setScriptUsages([]);
                }
            }
        };

        loadScriptUsages();
    }, [editMode, store.selectedScriptId, store]);

    React.useEffect(() => {
        const loadScript = async () => {
            if (editMode && store.selectedScriptId) {
                try {
                    const script = await store.loadScript(store.selectedScriptId);
                    if (script) {
                        form.setFieldsValue({
                            name: script.name,
                            body: script.body,
                            description: script.description
                        });

                        setRevisions(script.revisions || []);
                        setIsDataLoaded(true);
                    }
                } catch (error) {
                    console.error('Failed to load script:', error);
                }
            } else {
                form.resetFields();
                setRevisions([]);
                setIsDataLoaded(true);
            }
        };

        loadScript();
    }, [editMode, store.selectedScriptId, form, store]);

    if (!isDataLoaded) {
        return null;
    }

    const handleGoBack = () => {
        store.navigateToList();
    };

    const handleSave = async () => {
        try {
            await form.validateFields();
            const values = form.getFieldsValue();

            const scriptData = {
                name: values.name,
                body: values.body,
                description: values.description
            };

            let success = false;

            if (editMode && store.selectedScriptId) {
                success = await store.updateScript(store.selectedScriptId, scriptData);
            } else {
                success = await store.createScript(scriptData);
            }

            if (success) {
                handleGoBack();
            }
        } catch (error) {
            console.error('Validation failed:', error);
        }
    };

    const handleTestRun = async () => {
        if (form.getFieldValue('body').trim() === '') {
            message.error('SQL body is empty. Please enter SQL code to validate.');
            return;
        }

        store.openDryRunModal();
    };

    const handleViewRevision = (revision: ScriptRevision) => {
        setSelectedRevision(revision);
        store.openRevisionModal();
    };

    const handleCloseRevisionModal = () => {
        store.closeRevisionModal();
        setSelectedRevision(null);
    };

    const handleRestoreRevision = async (revision: ScriptRevision) => {
        if (!store.selectedScriptId) {
            message.error('No script selected');
            return;
        }

        try {
            const restoredScript = await store.restoreScriptRevision(store.selectedScriptId, revision.id);

            if (restoredScript) {
                form.setFieldsValue({
                    name: restoredScript.name,
                    body: restoredScript.body,
                    description: restoredScript.description
                });

                if (restoredScript.revisions) {
                    setRevisions(restoredScript.revisions);
                }

                handleCloseRevisionModal();
            }
        } catch (error) {
            console.error('Failed to restore revision:', error);
            message.error('Failed to restore revision');
        }
    };

    return (
        <Layout style={{ height: '100%', background: 'white' }}>
            <LayoutHeader
                helpMessage={
                    editMode
                        ? 'You can edit the script name, SQL body, and description.'
                        : 'Create a new SQL script by providing a name, SQL body, and description.'
                }
                title={editMode ? 'Edit Script' : 'Create Script'}
                buttons={[
                    <Button
                        key="scripts-cancel"
                        data-id="button-cancel"
                        className="light"
                        size="large"
                        onClick={handleGoBack}
                        disabled={store.isSaving}
                    >
                        Cancel
                    </Button>,
                    <Button key="scripts-validate" data-id="button-validate" size="large" onClick={handleTestRun}>
                        Dry Run
                    </Button>,
                    <Button
                        key="scripts-save"
                        data-id="button-save"
                        type="primary"
                        size="large"
                        onClick={handleSave}
                        loading={store.isSaving}
                        disabled={store.isLoading}
                    >
                        {editMode ? 'Save' : 'Create'}
                    </Button>
                ]}
            />
            <Content style={{ maxHeight: 'calc(100vh - 70px)', overflowY: 'auto' }}>
                <Form form={form} layout="vertical" className="create-edit-script">
                    <Spin tip={editMode ? 'Loading script...' : 'Creating script...'} spinning={store.isLoading}>
                        <Form.Item
                            name="name"
                            label="Name"
                            rules={[
                                { required: true, message: 'Please enter a script name' },
                                { max: 255, message: 'Name must be less than 255 characters' }
                            ]}
                        >
                            <Input placeholder="Enter script name" />
                        </Form.Item>

                        <Form.Item name="description" label="Description">
                            <Input.TextArea placeholder="Enter script description" rows={1} />
                        </Form.Item>

                        <Form.Item
                            name="body"
                            label="SQL Body"
                            rules={[{ required: true, message: 'Please enter SQL body' }]}
                        >
                            <AceEditor
                                mode="mysql"
                                theme="xcode"
                                name="sql-script-body-editor"
                                editorProps={{ $blockScrolling: true }}
                                value={form.getFieldValue('body')}
                                width="100%"
                                height="400px"
                                onChange={(content: string) => {
                                    form.setFieldsValue({ body: content });
                                }}
                                placeholder="Enter SQL query body"
                                setOptions={{
                                    enableBasicAutocompletion: true,
                                    enableLiveAutocompletion: true,
                                    enableSnippets: true,
                                    showLineNumbers: true,
                                    tabSize: 2
                                }}
                            />
                        </Form.Item>

                        {editMode && (
                            <SqlScriptUsages scriptUsages={scriptUsages} isLoadingUsages={store.isLoadingUsages} />
                        )}

                        {editMode && sortedRevisions.length > 0 && (
                            <ScriptRevisionHistory
                                store={store}
                                revisions={sortedRevisions}
                                onViewRevision={handleViewRevision}
                            />
                        )}
                    </Spin>
                </Form>
            </Content>
            <RevisionViewModal
                revision={selectedRevision}
                store={store}
                onClose={handleCloseRevisionModal}
                onRestore={handleRestoreRevision}
            />
            <DryRunModal store={store} getSqlBody={getSqlBody} />
        </Layout>
    );
};

export default observer(CreateEditScript);
